import type { DisplayCue } from "@expert/contentful";
import type { DeviceCues, SoftwareCues } from "@expert/shared-types";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";
import { deselectCue } from "@/sales-tools/detected-cues/services/deselectCue";
import { handleDetectedCue } from "@/sales-tools/detected-cues/services/handleDetectedCue";
import { setPreviousCues } from "@/sales-tools/detected-cues/services/setPreviousCues";

type DetectedCuesStore = {
    displayCuesBySession: Record<string, DisplayCue[]>;
    setDefaultPartnerCues: (sessionId: string, contentfulCues: DisplayCue[]) => DisplayCue[];
    processNewlyDetectedCue: (sessionId: string, newCues: (DeviceCues | SoftwareCues)[]) => void;
    processPreviouslyDetectedCues: (sessionId: string, previousCues: string[]) => void;
    deselectDetectedCue: (sessionId: string, cueToDeselect: string) => void;
    resetEvents: (sessionId: string) => void;
    getDisplayCues: (sessionId: string) => DisplayCue[];
};

export const useDetectedCuesStore = createWithEqualityFn<DetectedCuesStore>()(
    persist(
        immer(
            devtools((set, get) => ({
                displayCuesBySession: {},
                setDefaultPartnerCues: (sessionId, contentfulCues) => {
                    const cues = contentfulCues ?? [];
                    set((state) => {
                        state.displayCuesBySession[sessionId] = cues;
                    });
                    return cues;
                },
                processNewlyDetectedCue: (sessionId, newCues) => {
                    set((state) => {
                        const current = state.displayCuesBySession[sessionId] ?? [];
                        state.displayCuesBySession[sessionId] = handleDetectedCue(current, newCues);
                    });
                },
                processPreviouslyDetectedCues: (sessionId, previousCues) => {
                    set((state) => {
                        const current = state.getDisplayCues(sessionId) ?? [];
                        state.displayCuesBySession[sessionId] = setPreviousCues(current, previousCues);
                    });
                },
                deselectDetectedCue: (sessionId, cueToDeselect) => {
                    set((state) => {
                        const current = state.displayCuesBySession[sessionId] ?? [];
                        state.displayCuesBySession[sessionId] = deselectCue(current, cueToDeselect);
                    });
                },
                resetEvents: (sessionId) => {
                    set((state) => {
                        delete state.displayCuesBySession[sessionId];
                    });
                },
                getDisplayCues: (sessionId) => {
                    return get().displayCuesBySession[sessionId] ?? [];
                },
            })),
        ),
        { name: "detected-cues-store", skipHydration: true },
    ),
    shallow,
);
